import { Component } from '@theme/component';

// Touch devices have no hover to pre-warm a gallery before the first swipe.
// Idle-until-urgent: the watcher at the bottom of this file prepares the
// galleries around the viewport (±1.5 screens) whenever scrolling settles —
// the heavy work runs only while the user is idle, never competing with
// scrolling or the first images. Pointerdown hydration stays as the urgent
// fallback for a user faster than the settle window.
const TOUCH_PRIMARY = window.matchMedia?.('(hover: none)').matches ?? false;

// After a touch ends, iOS still runs its synthetic mouse sequence (mousedown,
// focus, mouseup, click) for the tap; nothing may rebuild the card's DOM in
// that window or WebKit drops the click. Deferred work waits this long.
const TOUCH_SETTLE_MS = 350;

// Hover-preview warm-up: on pointer devices the second image is fetched as soon as
// the card nears the viewport, so the hover swap paints from cache instead of
// showing a half-loaded slide. One shared observer for every card on the page.
const WARM_MARGIN = '50%';
/** @type {IntersectionObserver | null} */
let warmObserver = null;
const getWarmObserver = () => {
	if (warmObserver) return warmObserver;
	warmObserver = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				if (!entry.isIntersecting) continue;
				warmObserver?.unobserve(entry.target);
				/** @type {any} */ (entry.target).warmHoverImage?.();
			}
		},
		{ rootMargin: WARM_MARGIN }
	);
	return warmObserver;
};

/**
 * Product Card Web Component
 *
 * Handles hover image preview for product cards.
 * Cart interactions are delegated to action--cart--* blocks.
 *
 * @example
 * <product-card data-product-url="/products/my-product" data-product-id="123" data-hover-preview="true">
 *   <carousel-slider>...</carousel-slider>
 * </product-card>
 */

class ProductCard extends Component {
	/** @type {AbortController|null} */
	#abortController = null;

	/** @type {HTMLElement|null} */
	#carousel = null;

	/** @type {boolean} whether the deferred gallery slides have been revealed */
	#galleryHydrated = false;

	/**
	 * Canonical product URL. Integrations that treat the card as a product link
	 * read this (the Swish wishlist app derives product handle + variant from it).
	 * @returns {string}
	 */
	get href() {
		return this.dataset.productUrl ?? '';
	}

	/** @type {HTMLImageElement|null} pre-fetched copy of the hover image (keeps the request alive) */
	#warmImage = null;

	/** A finger is currently down on this card (touchstart … touchend/cancel). */
	#touching = false;

	/** Hydration was requested while touching — run it once the finger lifts. */
	#hydrationDeferred = false;

	/** When the last touch on this card ended (performance.now()); -Infinity if never. */
	#lastTouchEnd = -Infinity;

	/** @type {number} */
	#deferredHydrationTimer = 0;

	connectedCallback() {
		super.connectedCallback();
		this.#abortController = new AbortController();
		this.#setupTouchTracking();
		this.#setupInteractions();
		this.#setupNavigationFeedback();
		this.#recoverRecycledGallery();

		const media = this.querySelector('.product-card__media');
		if (!TOUCH_PRIMARY && media?.dataset.hoverPreview === 'true' && this.querySelector('template[data-card-deferred-slides]')) {
			getWarmObserver().observe(this);
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController?.abort();
		this.#abortController = null;
		clearTimeout(this.#deferredHydrationTimer);
		this.#touching = false;
		warmObserver?.unobserve(this);
	}

	/**
	 * Public (called by the shared warm observer): fetch the hover image ahead of
	 * time using the exact `srcset`/`sizes` of the deferred second slide, so the
	 * browser resolves the same candidate it will use once the slide is live and
	 * the hover swap hits the cache.
	 */
	warmHoverImage() {
		if (this.#warmImage) return;
		const template = this.querySelector('template[data-card-deferred-slides]');
		const source = template?.content.querySelector('img');
		if (!source) return;

		const image = new Image();
		image.fetchPriority = 'low';
		image.decoding = 'async';
		const sizes = source.getAttribute('sizes');
		const srcset = source.getAttribute('srcset');
		if (sizes) image.sizes = sizes;
		if (srcset) image.srcset = srcset;
		image.src = source.getAttribute('src') ?? '';
		this.#warmImage = image;
	}

	/**
	 * Recover a gallery that was already hydrated, then dehydrated + rehydrated by the grid
	 * virtualizer (VirtualChunks). The restored card keeps its expanded slides (no deferred
	 * template) but its carousel is a fresh, uninitialized instance — and the carousel's own
	 * lazy-init observer does NOT re-fire reliably for a reconnected, already-expanded slider.
	 * Without this, a card the user already browsed comes back from recycling with dead dots /
	 * a stuck slider. Detect that state on (re)connect and re-initialize it deterministically.
	 *
	 * Scoped so it never touches a fresh idle card: those have a single live slide + a deferred
	 * template (realSlides < 2), so they stay lazy until the first hover/touch.
	 */
	#recoverRecycledGallery() {
		const carousel = this.querySelector('carousel-slider');
		if (!carousel) return;
		if (this.querySelector('template[data-card-deferred-slides]')) return; // still lazy
		const realSlides = carousel.querySelectorAll('.carousel__slide:not([data-clone])').length;
		if (realSlides < 2) return; // single-slide gallery, nothing to drive

		this.#galleryHydrated = true;
		// Defer one frame so the carousel's own connectedCallback (refs/track setup) runs first.
		requestAnimationFrame(() => {
			if (!this.isConnected || carousel.initialized) return;
			if (typeof carousel.rehydrate === 'function') carousel.rehydrate();
		});
	}

	/**
	 * Public: hydrate + initialize this card's gallery so the first swipe is
	 * live. Idempotent. Called by the touch idle watcher below; interaction
	 * handlers use the same path internally.
	 */
	prepareGallery() {
		this.#hydrateGallery();
	}

	/**
	 * Immediate feedback when a product link in the card is activated: the
	 * media dims (CSS on product-card[data-navigating]) until the PDP paints.
	 * On a phone the PDP's first paint lands 1–2 s after the tap and the
	 * browser keeps showing the grid meanwhile, so without this the tap read
	 * as "nothing happened" and people tapped again. Cleared on pageshow for
	 * pages restored from the back/forward cache.
	 */
	#setupNavigationFeedback() {
		const signal = this.#abortController?.signal;
		this.addEventListener('click', (event) => {
			if (event.defaultPrevented || event.button !== 0) return;
			if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
			const target = event.target instanceof Element ? event.target : null;
			const link = target?.closest('a[href]');
			if (!link || !this.contains(link) || link.target === '_blank') return;
			if (!/\/products\//.test(link.getAttribute('href') || '')) return;
			this.setAttribute('data-navigating', '');
		}, { signal });
	}

	/**
	 * Track the finger so gallery hydration never mutates the DOM mid-tap
	 * (see #hydrateGallery). Touch events, not pointer events: a touch that
	 * turns into a page scroll ends with pointercancel, while touchend/
	 * touchcancel always close the pair.
	 */
	#setupTouchTracking() {
		if (!TOUCH_PRIMARY) return;
		const signal = this.#abortController?.signal;
		const end = () => {
			this.#touching = false;
			this.#lastTouchEnd = performance.now();
			if (!this.#hydrationDeferred) return;
			this.#hydrationDeferred = false;
			clearTimeout(this.#deferredHydrationTimer);
			// After the click has been dispatched (and the navigation, if it was
			// a tap on the product), with margin for a slow main thread.
			this.#deferredHydrationTimer = window.setTimeout(() => {
				if (this.isConnected && !this.#touching) this.#hydrateGallery();
			}, TOUCH_SETTLE_MS + 50);
		};
		this.addEventListener('touchstart', () => { this.#touching = true; }, { passive: true, signal });
		this.addEventListener('touchend', end, { passive: true, signal });
		this.addEventListener('touchcancel', end, { passive: true, signal });
	}

	/**
	 * Reveal the gallery's deferred slides (held in an inert <template> by the gallery
	 * snippet to keep the card light) into the carousel, then re-initialize it. Runs once,
	 * on the first hover/touch.
	 */
	#hydrateGallery() {
		if (this.#galleryHydrated) return;

		// Never rebuild the gallery under a finger. Hydration appends slides,
		// re-initializes the carousel (clones prepended, viewport scrollLeft
		// jumped) — a scroll container moving during a tap makes iOS Safari
		// drop the click, so the first tap on a product right after scrolling
		// (idle hydration lands ~250 ms after the scroll settles) went nowhere.
		// Deferred to just after touchend, once the click has been dispatched.
		if (this.#touching || performance.now() - this.#lastTouchEnd < TOUCH_SETTLE_MS) {
			this.#hydrationDeferred = true;
			return;
		}
		this.#galleryHydrated = true;

		const media = this.querySelector('.product-card__media');
		const template = media?.querySelector('template[data-card-deferred-slides]');
		if (!template) return;

		const carousel = this.#carousel || this.querySelector('carousel-slider');
		const track = carousel?.querySelector('.carousel__track');
		if (!carousel || !track) return;

		// Touch cap (gallery block "Max carousel images (touch devices)"): the
		// first slide is already live, so only maxMobile - 1 deferred slides are
		// revealed; the rest are dropped with the template. Fewer slides also
		// means fewer loop clones per card (2 clone sets × slides), which is what
		// keeps a long PLP light on a phone.
		const maxMobile = TOUCH_PRIMARY ? parseInt(media.dataset.maxImagesMobile, 10) || 0 : 0;
		if (maxMobile > 0) {
			const slides = template.content.querySelectorAll('.carousel__slide');
			for (let i = Math.max(0, maxMobile - 1); i < slides.length; i++) slides[i].remove();
		}

		// Touch: reveal the slides without their image URLs. Idle hydration puts
		// the whole gallery (plus loop clones) into the DOM while the user is NOT
		// looking at it, and browsers fetch lazy images within ~1 viewport in any
		// direction — so every extra slide of every card near the viewport was
		// downloaded (measured 174 image requests / 3 MB on a 24-product PLP).
		// The carousel restores src/srcset on the first swipe (#markInteracted)
		// and per slide as it preloads neighbours; the first slide stays live.
		if (TOUCH_PRIMARY) {
			for (const img of template.content.querySelectorAll('img')) {
				const src = img.getAttribute('src');
				const srcset = img.getAttribute('srcset');
				if (!src) continue;
				img.setAttribute('data-src', src);
				img.removeAttribute('src');
				if (srcset) {
					img.setAttribute('data-srcset', srcset);
					img.removeAttribute('srcset');
				}
			}
		}

		track.appendChild(template.content);
		template.remove();
		if (typeof carousel.rehydrate === 'function') carousel.rehydrate();
	}

	/**
	 * Wire up pointer interactions on the card media:
	 *   1. Gallery hydration (reveal deferred slides) on first hover/touch — always, whenever
	 *      a deferred template is present, regardless of the hover-preview setting. The carousel
	 *      needs its slides to swipe/show dots even when hover preview is disabled.
	 *   2. Hover preview (jump to the next image on desktop hover) — only when enabled.
	 */
	#setupInteractions() {
		const mediaContainer = this.querySelector('.product-card__media');
		if (!mediaContainer) return;

		// Hover preview is opt-in via the gallery snippet; hydration is needed whenever
		// the gallery deferred its extra slides into a <template>.
		const hoverPreview = mediaContainer.dataset.hoverPreview === 'true';
		const hasDeferredSlides = !!mediaContainer.querySelector('template[data-card-deferred-slides]');
		if (!hoverPreview && !hasDeferredSlides) return;

		const signal = this.#abortController?.signal;

		// Find the carousel (may be initialized later)
		const getCarousel = () => {
			if (!this.#carousel) {
				this.#carousel = this.querySelector('carousel-slider');
			}
			return this.#carousel;
		};

		// The hover image (second slide). Promoted to eager on first hover so the
		// browser fetches it even if the slide sits outside the lazy-load margin.
		let hoverImage = /** @type {HTMLImageElement|null} */ (null);
		const getHoverImage = () => {
			if (hoverImage) return hoverImage;
			const carousel = getCarousel();
			if (carousel && typeof carousel.getSlideNodes === 'function') {
				const img = carousel.getSlideNodes()[1]?.querySelector('img');
				if (img) {
					if (img.loading === 'lazy') img.loading = 'eager';
					hoverImage = img;
				}
			}
			return hoverImage;
		};

		// Incremented on every enter/leave: a swap that resolves after the pointer
		// already left must not fire.
		let hoverToken = 0;

		mediaContainer.addEventListener(
			'pointerenter',
			async (event) => {
				// Only respond to mouse, not touch
				if (event.pointerType !== 'mouse') return;

				// Reveal deferred slides + re-init the carousel before previewing.
				this.#hydrateGallery();

				if (!hoverPreview) return;

				const token = ++hoverToken;
				const carousel = getCarousel();
				if (!carousel || typeof carousel.scrollNext !== 'function') return;

				// Never swap to a slide that hasn't painted yet: wait for the image to be
				// decoded (instant when the warm-up already put it in cache), then jump.
				const image = getHoverImage();
				if (image && !(image.complete && image.naturalWidth > 0)) {
					try {
						await image.decode();
					} catch {
						// Decode can reject for a still-loading image; fall back to the load event.
						await new Promise((resolve) => {
							image.addEventListener('load', resolve, { once: true });
							image.addEventListener('error', resolve, { once: true });
						});
					}
					if (token !== hoverToken) return;
				}

				// Jump to next slide instantly (no animation)
				carousel.scrollNext(true);
			},
			{ signal }
		);

		mediaContainer.addEventListener(
			'pointerleave',
			(event) => {
				// Only respond to mouse, not touch
				if (event.pointerType !== 'mouse') return;
				if (!hoverPreview) return;

				hoverToken += 1;
				const carousel = getCarousel();
				if (carousel && typeof carousel.scrollTo === 'function') {
					// Jump back to first slide instantly (no animation)
					carousel.scrollTo(0, true);
				}
			},
			{ signal }
		);

		// Touch/pen: ask for the deferred slides on first touch. With a finger
		// down this is deferred to touchend (#hydrateGallery), so the first
		// swipe on a never-prepared card scrolls the page; the idle watcher at
		// the bottom of this file prepares cards around the viewport before
		// that in the common case.
		mediaContainer.addEventListener(
			'pointerdown',
			(event) => {
				if (event.pointerType === 'mouse') return;
				this.#hydrateGallery();
			},
			{ signal }
		);

		// Keyboard users get no hover event — hydrate when focus enters the card.
		// Keyboard focus only. iOS Safari focuses the tapped link during the
		// synthetic mouse sequence it runs after touchend (mousedown → focus →
		// mouseup → click): hydrating here rebuilt the carousel between the
		// finger lifting and the click, and WebKit dropped the click — the
		// first tap on a card "did nothing" (the wishlist icon showing via
		// :focus-within was the tell). :focus-visible is false for that
		// pointer-driven focus and true for Tab.
		this.addEventListener('focusin', (event) => {
			if (event.target instanceof Element && event.target.matches(':focus-visible')) this.#hydrateGallery();
		}, { signal });
	}

}

// Register custom element
if (!customElements.get('product-card')) {
	customElements.define('product-card', ProductCard);
}

// ─── Touch idle init (idle-until-urgent) ───
// Preparing at settle keeps gallery work off the scroll path entirely. The
// margin covers more than the visible cards: a swipe can land mid-momentum
// or right after the next scroll stretch, before any new settle fires — and
// the pointerdown fallback is a coin flip (the compositor picks the gesture's
// scroll target from the pre-hydration state, when the track has one slide
// and no overflow). With ±1.5 viewports prepared at every settle, the finger
// lands on a live carousel. Staggered one card per frame: a batch of clone
// builds + reflows never blocks a scroll that starts mid-preparation.
if (TOUCH_PRIMARY) {
	const SCROLL_SETTLE_MS = 150;
	const PREPARE_MARGIN_VH = 150;
	let settleTimer = 0;

	const prepareAround = () => {
		const margin = (PREPARE_MARGIN_VH / 100) * window.innerHeight;
		const queue = [];
		for (const card of document.querySelectorAll('product-card')) {
			if (!card.querySelector('template[data-card-deferred-slides]')) continue;
			const rect = card.getBoundingClientRect();
			if (rect.bottom <= -margin || rect.top >= window.innerHeight + margin) continue;
			const dist = rect.bottom < 0 ? -rect.bottom : Math.max(0, rect.top - window.innerHeight);
			queue.push({ card, dist });
		}
		queue.sort((a, b) => a.dist - b.dist);
		const step = () => {
			const item = queue.shift();
			if (!item) return;
			item.card.prepareGallery?.();
			if (queue.length) requestAnimationFrame(step);
		};
		step();
	};

	// Capture phase: scroll events don't bubble, but capture descends to the
	// scrolling element — this also catches inner scroll containers (search
	// overlay results, drawers), where cards render while page scroll never
	// fires. prepareAround is idempotent, so extra triggers are no-ops.
	window.addEventListener('scroll', () => {
		clearTimeout(settleTimer);
		settleTimer = window.setTimeout(prepareAround, SCROLL_SETTLE_MS);
	}, { passive: true, capture: true });

	// New cards can land without a page scroll (deferred reveal, infinite
	// scroll append, filter morph) — prepare the fresh stretch too. Capture:
	// the grid dispatches on itself and the event doesn't bubble.
	document.addEventListener('collection:products-appended', () => {
		clearTimeout(settleTimer);
		settleTimer = window.setTimeout(prepareAround, SCROLL_SETTLE_MS);
	}, true);

	// Initial viewport: prepare once the page has fully loaded AND the main
	// thread is idle. Running any earlier puts hydration/init work inside the
	// load window, where it scores as Total Blocking Time and competes with
	// image decoding — a swipe in those first seconds still works via the
	// pointerdown fallback.
	const initialPrepare = () => {
		'requestIdleCallback' in window
			? requestIdleCallback(prepareAround, { timeout: 4000 })
			: setTimeout(prepareAround, 1500);
	};
	if (document.readyState === 'complete') {
		initialPrepare();
	} else {
		window.addEventListener('load', initialPrepare, { once: true });
	}
}

// A page restored from the back/forward cache comes back exactly as it was
// left — including the card that was dimmed for the navigation.
window.addEventListener('pageshow', (event) => {
	if (!event.persisted) return;
	for (const card of document.querySelectorAll('product-card[data-navigating]')) {
		card.removeAttribute('data-navigating');
	}
});
